import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { DarkTiles } from "@/components/map/DarkTiles";
import { LocateFixed } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { DAKAR_CENTER, roleIcon } from "./roleMarkers";
import { GeoPermissionNotice } from "./GeoPermissionNotice";

// Centre au premier fix GPS (sans animation), puis suit en panoramique si follow=true
const AutoCenter = ({ center, follow }) => {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (!center) return;
    if (!done.current) {
      map.setView(center, 15, { animate: false });
      done.current = true;
    } else if (follow) {
      map.panTo(center, { animate: true, duration: 0.5 });
    }
  }, [center?.[0], center?.[1], follow, map]); // eslint-disable-line react-hooks/exhaustive-deps
  // Stoppe toute animation en cours si la carte est démontée (évite l'erreur _leaflet_pos)
  useEffect(() => () => { try { map.stop(); } catch { /* carte déjà détruite */ } }, [map]);
  return null;
};

const RecenterButton = ({ center, onRequest }) => {
  const map = useMap();
  return (
    <button
      type="button"
      onClick={() => (center ? map.setView(center, 16, { animate: true }) : onRequest?.())}
      className="absolute top-3 right-3 z-[500] w-11 h-11 rounded-full bg-[#0F172A]/90 backdrop-blur-md border border-[#D4AF37]/50 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
      data-testid="map-recenter-btn"
      aria-label="Recentrer sur ma position"
    >
      <LocateFixed size={18} className="text-[#D4AF37]" />
    </button>
  );
};

/**
 * Carte WÔMA (style sombre de l'accueil). `self` = position de l'utilisateur, `role` = marqueur.
 */
export const WomaMap = ({ self, role = "client", status, onRequest, follow = false, height = 280, children, testId = "woma-map" }) => {
  const center = self ? [self.lat, self.lng] : null;
  const selfIcon = useMemo(() => roleIcon(role, { pulse: role !== "dot", size: role === "dot" ? 22 : 40 }), [role]);
  return (
    <div className="relative z-0 isolate rounded-[2rem] overflow-hidden border border-white/10" style={{ height }} data-testid={testId}>
      <MapContainer
        center={center || DAKAR_CENTER}
        zoom={center ? 15 : 12}
        className="woma-dark-map"
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
        attributionControl={false}
      >
        <DarkTiles />
        <AutoCenter center={center} follow={follow} />
        {center && <Marker position={center} icon={selfIcon} />}
        {children}
        <RecenterButton center={center} onRequest={onRequest} />
      </MapContainer>
      <GeoPermissionNotice status={status} onRetry={onRequest} />
    </div>
  );
};
